import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { GroupSummary } from '../state/groups';
import { createGroup, joinGroupByCode } from '../shared/groups';

type Props = {
  groups: GroupSummary[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onChanged: () => void;
};

type InviteTarget = { id: string; name: string; code: string };

// Minimal group management: switch between existing groups, create new by name,
// join by code. After creation we drop into an invite step so the creator can
// share the join link with the people they want in the group.
export function GroupPicker({ groups, activeId, onSelect, onChanged }: Props) {
  const [mode, setMode] = useState<'pick' | 'create' | 'join' | 'invite'>('pick');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InviteTarget | null>(null);
  const [copied, setCopied] = useState(false);

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const id = await createGroup(name);
      const { data, error: qErr } = await supabase
        .from('groups')
        .select('join_code')
        .eq('id', id)
        .single();
      if (qErr) throw qErr;
      onChanged();
      setInvite({ id, name: name.trim(), code: (data?.join_code as string) ?? '' });
      setMode('invite');
      setName('');
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function copyInviteLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  async function shareInvite(target: InviteTarget, url: string) {
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    if (nav.share) {
      try {
        await nav.share({
          title: `Join "${target.name}" on Cupwire`,
          text: `Join me on Cupwire — ${target.name}. Use code ${target.code} or tap:`,
          url,
        });
        return;
      } catch {
        // Fallthrough to clipboard copy if share was cancelled or failed.
      }
    }
    copyInviteLink(url);
  }

  async function submitJoin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const id = await joinGroupByCode(code);
      onChanged();
      onSelect(id);
      setMode('pick');
      setCode('');
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gp-wrap">
      {mode === 'pick' && (
        <>
          <ul className="gp-list">
            {groups.map((g) => (
              <li key={g.id} className="gp-row-wrap">
                <button
                  className={`gp-row ${g.id === activeId ? 'gp-row-active' : ''}`}
                  onClick={() => onSelect(g.id)}
                >
                  <span className="gp-name">{g.name}</span>
                  <span className="gp-code">{g.join_code}</span>
                </button>
                <button
                  className="gp-row-invite"
                  onClick={() => {
                    setInvite({ id: g.id, name: g.name, code: g.join_code });
                    setCopied(false);
                    setMode('invite');
                  }}
                  aria-label={`Invite people to ${g.name}`}
                >
                  Invite
                </button>
              </li>
            ))}
            {groups.length === 0 && (
              <li className="gp-empty">You're not in any groups yet.</li>
            )}
          </ul>
          <div className="gp-actions">
            <button onClick={() => setMode('create')}>Create group</button>
            <button onClick={() => setMode('join')}>Join by code</button>
          </div>
        </>
      )}

      {mode === 'create' && (
        <form onSubmit={submitCreate} className="gp-form">
          <input
            placeholder="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={40}
            required
          />
          {error && <p className="gp-error">{error}</p>}
          <div className="gp-form-actions">
            <button type="button" onClick={() => setMode('pick')}>
              Cancel
            </button>
            <button type="submit" disabled={busy} className="gp-primary">
              {busy ? '…' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {mode === 'invite' && invite && (() => {
        const url = `${window.location.origin}/?join=${encodeURIComponent(invite.code)}`;
        return (
          <div className="gp-form">
            <div className="gp-invite-title">Group created · "{invite.name}"</div>
            <div className="gp-invite-sub">Share this link or code to add people.</div>
            <div className="gp-invite-code">{invite.code}</div>
            <div className="gp-invite-link">{url}</div>
            <div className="gp-form-actions">
              <button type="button" onClick={() => copyInviteLink(url)}>
                {copied ? 'Copied ✓' : 'Copy link'}
              </button>
              <button
                type="button"
                className="gp-primary"
                onClick={() => shareInvite(invite, url)}
              >
                Share invite
              </button>
            </div>
            <button
              type="button"
              className="gp-invite-done"
              onClick={() => {
                setInvite(null);
                setMode('pick');
              }}
            >
              Done
            </button>
          </div>
        );
      })()}

      {mode === 'join' && (
        <form onSubmit={submitJoin} className="gp-form">
          <input
            placeholder="Join code (e.g. FAMIL-A3F7)"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            autoFocus
            maxLength={20}
            required
          />
          {error && <p className="gp-error">{error}</p>}
          <div className="gp-form-actions">
            <button type="button" onClick={() => setMode('pick')}>
              Cancel
            </button>
            <button type="submit" disabled={busy} className="gp-primary">
              {busy ? '…' : 'Join'}
            </button>
          </div>
        </form>
      )}

      <style>{`
        .gp-wrap { padding: 16px; }
        .gp-list { list-style: none; padding: 0; margin: 0 0 12px; }
        .gp-row-wrap {
          display: flex;
          gap: 8px;
          align-items: stretch;
          margin-bottom: 8px;
        }
        .gp-row {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: var(--surface);
          color: var(--text);
          border: 1px solid var(--divider);
          border-radius: 10px;
          padding: 14px;
          font-size: 15px;
        }
        .gp-row-invite {
          background: transparent;
          color: var(--accent);
          border: 1px solid var(--accent);
          border-radius: 10px;
          padding: 0 14px;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }
        .gp-row-active {
          border-color: var(--accent);
          background: var(--surface-alt);
        }
        .gp-name { font-weight: 600; }
        .gp-code { color: var(--text-muted); font-size: 12px; font-family: ui-monospace, monospace; }
        .gp-empty { color: var(--text-muted); font-size: 13px; text-align: center; padding: 16px; }
        .gp-actions { display: flex; gap: 10px; }
        .gp-actions button {
          flex: 1;
          background: var(--surface);
          color: var(--accent);
          border: 1px solid var(--accent);
          border-radius: 10px;
          padding: 12px;
          font-size: 14px;
          font-weight: 600;
        }
        .gp-form { display: flex; flex-direction: column; gap: 12px; }
        .gp-form input {
          background: var(--surface);
          color: var(--text);
          border: 1px solid var(--divider);
          border-radius: 10px;
          padding: 14px;
          font-size: 16px;
        }
        .gp-form input:focus { outline: none; border-color: var(--accent); }
        .gp-error { color: var(--error); font-size: 13px; margin: 0; }
        .gp-form-actions { display: flex; gap: 10px; }
        .gp-form-actions button {
          flex: 1;
          background: transparent;
          color: var(--text-muted);
          border: 1px solid var(--divider);
          border-radius: 10px;
          padding: 12px;
          font-size: 14px;
        }
        .gp-primary {
          background: var(--accent) !important;
          color: var(--bg) !important;
          border-color: var(--accent) !important;
          font-weight: 700;
        }
        .gp-invite-title { color: var(--text); font-size: 16px; font-weight: 700; }
        .gp-invite-sub { color: var(--text-muted); font-size: 13px; margin-bottom: 8px; }
        .gp-invite-code {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 22px;
          font-weight: 700;
          color: var(--accent);
          background: var(--surface);
          border: 1px solid var(--divider);
          border-radius: 10px;
          padding: 14px;
          text-align: center;
          letter-spacing: 2px;
        }
        .gp-invite-link {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 11px;
          color: var(--text-muted);
          background: var(--surface);
          border: 1px solid var(--divider);
          border-radius: 8px;
          padding: 10px;
          word-break: break-all;
        }
        .gp-invite-done {
          background: transparent;
          color: var(--text-muted);
          border: none;
          padding: 10px;
          font-size: 13px;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
}
